//
// Copyright 2024 DXOS.org
//

import { asyncTimeout } from '@dxos/async';
import { type Obj } from '@dxos/echo';
import { type AnyProperties } from '@dxos/echo/internal';

import { type EntityManager } from '../core-db/index.ts';
import { getObjectCore } from '../echo-handler/index.ts';

/**
 * @param obj
 */
// TODO(burdon): Rename/review SDK.
export const loadObject = <T extends AnyProperties>(obj: T): T => {
  return getObjectCore(obj).getDecoded(['data']) as any;
};

/**
 * EXPERIMENTAL - the API is subject to change.
 * @param objOrArray - an echo object or collection of objects with references to other echo objects.
 * @param valueAccessor - selector for a reference that needs to be loaded.
 *                        if return type is an array the method exits when all entries are non-null.
 *                        otherwise the method exits when valueAccessor is not null.
 * @param timeout - loading timeout, defaults to 5s.
 *
 * @deprecated Use `await Ref.load()` instead.
 */
// TODO(burdon): Rename/review SDK.
export const loadObjectReferences = async <
  T extends Obj.Unknown,
  RefType,
  DerefType = RefType extends Array<infer U> ? Array<NonNullable<U>> : NonNullable<RefType>,
>(
  // TODO(burdon): Must be T OR T[] not either.
  objOrArray: T | T[],
  valueAccessor: (obj: T) => RefType,
  { timeout }: { timeout: number } = { timeout: 5_000 },
): Promise<T extends T[] ? Array<DerefType> : DerefType> => {
  const objectArray = Array.isArray(objOrArray) ? objOrArray : [objOrArray];
  const tasks = objectArray.map((obj) => {
    const core = getObjectCore(obj as any);
    const value = valueAccessor(obj);
    const db = core.entityManager as EntityManager | undefined;
    if (db == null) {
      return value;
    }

    const isLoadedPredicate = Array.isArray(value)
      ? () => (valueAccessor(obj) as any[]).every((v) => v != null)
      : () => valueAccessor(obj) != null;
    if (isLoadedPredicate()) {
      return value;
    }

    // TODO(burdon): Timeout if trying to load object that isn't there.
    return asyncTimeout(
      db._updateEvent.waitFor(() => isLoadedPredicate()).then(() => valueAccessor(obj)),
      timeout,
    );
  });

  const result = await Promise.all(tasks);
  return (Array.isArray(objOrArray) ? result : result[0]) as any;
};
